/**
 * Portal News — admin + employee hooks.
 *
 * Admin routes hit `/v1/portal-news/*` (JwtGuard + CASL 'PortalNews').
 * The employee feed hits `/v1/portal/news` (PortalJwtGuard).
 *
 * Zero FE transformation: every response narrows directly into the
 * canonical types from `lib/types/portal-news.ts`.
 */

import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchPortalApi } from '@/lib/api/portal-fetch';
import { portalAdminFetch } from '@/lib/portal/api';
import type {
  PortalNews,
  PortalNewsListResponse,
  PortalNewsStatus,
  UpsertPortalNewsPayload,
  BulkDeletePortalNewsPayload,
  BulkDeletePortalNewsResponse,
} from '@/lib/types/portal-news';

// ── Query key factory ────────────────────────────────────────────────────────

export const portalNewsKeys = {
  all: () => ['portal-news'] as const,
  list: (params?: { status?: string; page?: number; limit?: number }) =>
    [...portalNewsKeys.all(), 'list', params ?? {}] as const,
  byId: (id: string) => [...portalNewsKeys.all(), 'detail', id] as const,
  // Employee-facing feed does not vary by admin mode (uses portal JWT, not admin JWT).
  feed: () => ['portal-news', 'feed'] as const,
  feedItem: (id: string) => ['portal-news', 'feed', id] as const,
};

function buildQuery(params: Record<string, unknown> | undefined): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  const qp = new URLSearchParams();
  for (const [k, v] of entries) qp.set(k, String(v));
  return `?${qp.toString()}`;
}

// ── Admin hooks ──────────────────────────────────────────────────────────────

export function usePortalNewsList(params?: {
  status?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: portalNewsKeys.list(params),
    queryFn: () =>
      portalAdminFetch<PortalNewsListResponse>(`portal-news${buildQuery(params)}`),
    placeholderData: keepPreviousData,
    select: (raw): PortalNewsListResponse => ({
      rows: raw.rows ?? [],
      total: raw.total ?? 0,
    }),
  });
}

export function usePortalNews(id: string) {
  return useQuery({
    queryKey: portalNewsKeys.byId(id),
    queryFn: () => portalAdminFetch<PortalNews>(`portal-news/${id}`),
    enabled: !!id,
  });
}

export function useCreatePortalNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpsertPortalNewsPayload) =>
      portalAdminFetch<PortalNews>('portal-news', {
        method: 'POST',
        body: JSON.stringify(dto),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: portalNewsKeys.all() });
    },
  });
}

export function useUpdatePortalNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpsertPortalNewsPayload }) =>
      portalAdminFetch<PortalNews>(`portal-news/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(dto),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: portalNewsKeys.all() });
      void qc.invalidateQueries({ queryKey: portalNewsKeys.byId(vars.id) });
    },
  });
}

export function useDeletePortalNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      portalAdminFetch<void>(`portal-news/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: portalNewsKeys.all() });
    },
  });
}

export function useBulkDeletePortalNews() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkDeletePortalNewsPayload) =>
      portalAdminFetch<BulkDeletePortalNewsResponse>('portal-news/bulk-delete', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: portalNewsKeys.all() });
    },
  });
}

function useSetStatus(verb: 'publish' | 'unpublish' | 'archive') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      portalAdminFetch<PortalNews>(`portal-news/${id}/${verb}`, { method: 'PATCH' }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: portalNewsKeys.all() });
      void qc.invalidateQueries({ queryKey: portalNewsKeys.byId(id) });
    },
  });
}

export function usePublishPortalNews() {
  return useSetStatus('publish');
}

export function useUnpublishPortalNews() {
  return useSetStatus('unpublish');
}

export function useArchivePortalNews() {
  return useSetStatus('archive');
}

// ── Employee-facing hooks ────────────────────────────────────────────────────

/**
 * Employee feed — consumes PortalJwtGuard-protected endpoint
 * `/v1/portal/news`. Server-side filters handle publish-window + targeting.
 */
export function usePortalNewsFeed() {
  return useQuery({
    queryKey: portalNewsKeys.feed(),
    // Employee feed — must use the portal JWT. fetchApi would send the
    // admin bearer (or none) which the BE PortalJwtGuard rejects with 401.
    queryFn: () => fetchPortalApi<PortalNews[]>('/portal/news'),
    placeholderData: keepPreviousData,
  });
}

/**
 * Employee article — increments viewCount server-side.
 */
export function usePortalNewsFeedItem(id: string) {
  return useQuery({
    queryKey: portalNewsKeys.feedItem(id),
    queryFn: () => fetchPortalApi<PortalNews>(`/portal/news/${id}`),
    enabled: !!id,
  });
}

// ── Filtering helper (optional client-side convenience) ─────────────────────

export function filterPortalNewsByStatus(
  rows: PortalNews[] | undefined,
  status: PortalNewsStatus,
): PortalNews[] {
  return (rows ?? []).filter((n) => n.status === status);
}
